import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LoadingSpinner } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import useConfig from '../../../utils/useConfig';
import MatchupPitcherSelector from './MatchupPitcherSelector';
import MatchupReport from './MatchupReport';
import PastMatchupsReport from './PastMatchupsReport';
import { isLhb } from '../../../data';

const StyledMatchupReportHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;
    > h3 {
        margin-bottom: 0;
    }
    > div {
        display: flex;
        align-items: center;
    }
`;

const MatchupReportBase = ({ isPlayerLoading, player, playerTeamStartingPitchers, isPastMatchups, isOpposingPitcherVersion }) => {
    const { t: translate } = useTranslation();
    const userPlayer = usePlayerSelector();
    const { CurrentYear } = useConfig();
    const [matchupPitcher, setMatchupPitcher] = useState();
    const [matchupBatter, setMatchupBatter] = useState();
    const isCatcherViewingOpponentHitters = userPlayer.IsCatcher && !isOpposingPitcherVersion;
    useEffect(() => {
        if (player) {
            if (isCatcherViewingOpponentHitters) {
                setMatchupBatter(player);
                if (playerTeamStartingPitchers?.length) {
                    setMatchupPitcher({
                        ...playerTeamStartingPitchers[0],
                        name: playerTeamStartingPitchers[0].FullName,
                        value: playerTeamStartingPitchers[0].PlayerID,
                    });
                }
            } else if (userPlayer.IsPitcher) {
                setMatchupBatter(player);
                setMatchupPitcher(userPlayer);
            } else {
                setMatchupBatter(userPlayer);
                setMatchupPitcher(player);
            }
        }
    }, [player]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isPlayerLoading) return <LoadingSpinner />;
    return (
        <>
            <StyledMatchupReportHeader>
                <div>
                    {isCatcherViewingOpponentHitters && userPlayer && userPlayer.TeamID ? (
                        <>
                            <b className="me-1">{translate('common.select-a-pitcher')}:</b>
                            <MatchupPitcherSelector
                                onSelect={setMatchupPitcher}
                                showGenericLhhRhh={!isPastMatchups}
                            />
                        </>
                    ) : isCatcherViewingOpponentHitters && <>{translate('sections.opponent.player.logged-in-no-id')}</>}
                </div>
            </StyledMatchupReportHeader>
            {matchupPitcher?.PlayerID && matchupBatter?.PlayerID && !isPastMatchups && (
                <MatchupReport
                    pitcher={matchupPitcher}
                    pitcherId={matchupPitcher.PlayerID}
                    batter={matchupBatter}
                    batterId={matchupBatter.PlayerID}
                    batterName={matchupBatter.FullName}
                    currentYear={CurrentYear}
                    isLHB={isLhb(matchupPitcher, matchupBatter)}
                    isLHP={matchupPitcher.Throws === 2}
                    isGenericHandednessPlayer={matchupPitcher.isGenericHandednessPlayer || false}
                    szTop={matchupBatter.PlayerStrikeZoneABS.StrikezoneAbsTop}
                    szBottom={matchupBatter.PlayerStrikeZoneABS.StrikezoneAbsBottom}
                />
            )}
            {matchupPitcher?.PlayerID && matchupBatter?.PlayerID && isPastMatchups && (
                <PastMatchupsReport
                    isOpposingPitcherVersion={isOpposingPitcherVersion}
                    pitcher={matchupPitcher}
                    pitcherId={matchupPitcher.PlayerID}
                    batter={matchupBatter}
                    batterId={matchupBatter.PlayerID}
                    currentYear={CurrentYear}
                    isLHB={isLhb(matchupPitcher, matchupBatter)}
                    isLHP={matchupPitcher.Throws === 2}
                />
            )}
        </>
    );
};

MatchupReportBase.propTypes = {
    isPlayerLoading: PropTypes.bool.isRequired,
    player: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
    }),
    playerTeamStartingPitchers: PropTypes.arrayOf(PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.string.isRequired,
    })),
    isPastMatchups: PropTypes.bool.isRequired,
    isOpposingPitcherVersion: PropTypes.bool,
};
MatchupReportBase.defaultProps = {
    player: null,
    playerTeamStartingPitchers: undefined,
    isOpposingPitcherVersion: false,
};

export default MatchupReportBase;
